import React from 'react'
import {
  ArrowTrendingUpIcon, BoltIcon, ChartBarIcon, CubeIcon, FireIcon, FolderOpenIcon, KeyIcon, SunIcon, SwatchIcon,
  TruckIcon, UserIcon,
} from '@heroicons/react/24/outline'
import { CENNIK, promocjaTrwa } from '../../config/plany'
import { formatKwota } from '../../utils'

/**
 * Wspólne elementy okna „Przejdź na Premium” i strony `/cennik` (nowy wygląd 2026-10-19, prośba właściciela):
 * ikony funkcji, przełącznik okresu rozliczenia, cena Premium i rysunek liczby kont.
 * Kolor niesie mały znacznik, tło zostaje białe (second-brain/10_PAMIEC_AGENTA/wyglad-bez-llm-kafelkow.md).
 */

/** Ikona funkcji Premium; klucze jak w `FUNKCJE_PREMIUM` (config/plany.js). */
export const IKONY_FUNKCJI = {
  projekt3d: CubeIcon,
  historia_cen_w_zestawach: ArrowTrendingUpIcon,
  zgloszenie_osd: BoltIcon,
  zawiadomienie_psp: FireIcon,
  teczka_pme2: FolderOpenIcon,
  zamowienia: TruckIcon,
  pogoda: SunIcon,
  uprawnienia_zespolu: KeyIcon,
  statystyki_zespolu: ChartBarIcon,
  marka_dokumentu: SwatchIcon,
}

export const OKRESY = [
  { klucz: 'miesiac', nazwa: 'Miesięcznie' },
  { klucz: 'rok', nazwa: 'Rocznie', dopisek: '2 miesiące gratis' },
]

/** Okres rozliczenia: dwa przyciski w jednej pigułce. */
export const PrzelacznikOkresu = ({ okres, onZmiana, pelny = false, className = '' }) => (
  <div role="radiogroup" aria-label="Okres rozliczenia"
       className={`${pelny ? 'flex w-full' : 'inline-flex'} rounded-full border border-gray-200 bg-gray-100 p-1 ${className}`}>
    {OKRESY.map((o) => {
      const on = okres === o.klucz
      return (
        <button key={o.klucz} type="button" role="radio" aria-checked={on} onClick={() => onZmiana(o.klucz)}
                className={`inline-flex min-h-[36px] items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold transition
                            ${pelny ? 'flex-1' : ''} ${on ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
          {o.nazwa}
          {o.dopisek && (
            <span className="whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">{o.dopisek}</span>
          )}
        </button>
      )
    })}
  </div>
)

/** Cena Premium w okresie. Plan miesięczny w czasie promocji: 99 zł, a obok przekreślona cena zwykła. */
export const cenaPremium = (okres, dzis = new Date()) => {
  if (okres === 'rok') {
    return {
      kwota: CENNIK.premiumRocznie, przed: null, za: 'netto rocznie za firmę',
      opis: `${formatKwota(CENNIK.premiumRocznie / 12, 2)} miesięcznie, płacisz za 10 miesięcy`,
    }
  }
  if (promocjaTrwa(dzis)) {
    return {
      kwota: CENNIK.promocja.cena, przed: CENNIK.premiumMiesiecznie, za: 'netto miesięcznie za firmę',
      opis: `przez pierwsze ${CENNIK.promocja.miesiecy} miesięcy, potem ${formatKwota(CENNIK.premiumMiesiecznie)}`,
      promocja: true,
    }
  }
  return { kwota: CENNIK.premiumMiesiecznie, przed: null, za: 'netto miesięcznie za firmę', opis: null }
}

/** Ostatni dzień promocji słownie, np. „31 grudnia 2026”. */
const koniecPromocji = () => {
  const [r, m, d] = CENNIK.promocja.zalozoneDo.split('-').map(Number)
  return new Date(r, m - 1, d).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })
}

export const CenaPremium = ({ okres, duza = false }) => {
  const c = cenaPremium(okres)
  return (
    <div>
      <p className="m-0 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {c.przed && (
          <span className="text-lg font-semibold text-gray-400 line-through decoration-2">
            <span className="sr-only">zamiast </span>{formatKwota(c.przed)}
          </span>
        )}
        <span className={`font-bold tracking-[-0.02em] text-gray-900 ${duza ? 'text-[44px] leading-none' : 'text-[34px] leading-none'}`}>
          {formatKwota(c.kwota)}
        </span>
        <span className="text-sm text-gray-500">{c.za}</span>
      </p>
      {c.opis && (
        <p className="m-0 mt-2 flex items-center gap-2 text-sm text-gray-700">
          {c.promocja && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-amber-400" aria-hidden="true" />}
          {c.promocja ? `Promocja do ${koniecPromocji()}: ${c.opis}` : c.opis}
        </p>
      )}
    </div>
  )
}

/** Liczba kont w firmie jako rząd osób; `pakiet` dorysowuje miejsce na kolejne konta. */
export const RysunekKont = ({ liczba, pakiet = false, podpis }) => (
  <div className="flex items-center gap-3">
    <div className="flex -space-x-1.5" aria-hidden="true">
      {Array.from({ length: liczba }, (_, i) => (
        <span key={i} className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-blue-600 text-white">
          <UserIcon className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
      ))}
      {pakiet && (
        <span className="flex h-7 items-center justify-center rounded-full border-2 border-dashed border-blue-300 bg-white px-1.5 text-[11px] font-bold text-blue-700">
          +{CENNIK.pakietKont.kont}
        </span>
      )}
    </div>
    <span className="text-sm text-gray-700">{podpis}</span>
  </div>
)
